import { motion } from "framer-motion";
import { Eyebrow, Reveal } from "./Reveal";

const JOBS = [
  {
    title: "Curbside brick debris",
    frames: [
      { src: "/img/curbside-brick-debris-removal-before-greenville-sc.webp", label: "Before", alt: "Before junk removal: broken brick debris dumped by the curb in Greenville SC" },
      { src: "/img/curbside-brick-debris-removal-after-greenville-sc.webp", label: "After", alt: "After junk removal: clean lawn by the curb in Greenville SC" },
    ],
  },
  {
    title: "Construction debris by the house",
    frames: [
      { src: "/img/construction-debris-removal-before-greenville-sc.webp", label: "Before", alt: "Before construction debris removal: drywall, tile and brick piled against a brick house" },
      { src: "/img/construction-debris-removal-after-greenville-sc.webp", label: "After", alt: "After construction debris removal: clean lawn behind the brick house" },
    ],
  },
  {
    title: "Overgrown yard & brick pile",
    frames: [
      { src: "/img/overgrown-yard-waste-removal-before-greenville-sc.webp", label: "Before", alt: "Before yard waste removal: overgrown brush and debris along a back fence" },
      { src: "/img/brick-pile-yard-cleanup-before-greenville-sc.webp", label: "Before", alt: "Before yard cleanup: brick pile and overgrown brush beside a shed" },
      { src: "/img/yard-waste-removal-after-greenville-sc.webp", label: "After", alt: "After yard waste removal: back yard cleared along the fence" },
      { src: "/img/backyard-cleanup-after-greenville-sc.webp", label: "After", alt: "After backyard cleanup: yard beside the shed cleared of brush and brick" },
    ],
  },
];

const Frame = ({ f, delay, testid, wide }) => (
  <motion.figure
    data-testid={testid}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    className={`group relative w-full overflow-hidden rounded-2xl bg-[#0D0D0B] sm:aspect-[3/4] sm:rounded-3xl ${wide ? "col-span-2 aspect-[4/3] sm:col-span-1" : "aspect-[3/4]"}`}
  >
    <img src={f.src} alt={f.alt} loading="lazy" decoding="async" width="480" height="640" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
    <figcaption className={`absolute left-3 top-3 rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.2em] sm:left-4 sm:top-4 sm:px-4 sm:py-1.5 sm:text-xs ${f.label === "Before" ? "bg-[#0D0D0B] text-[#F2EEE6]" : "bg-[#FFB000] text-[#0D0D0B]"}`}>
      {f.label}
    </figcaption>
  </motion.figure>
);

const Job = ({ job, i }) => (
  <div data-testid={`before-after-job-${i}`} className="border-t-2 border-[#0D0D0B] pt-6">
    <Reveal>
      <div className="mb-6 flex items-baseline gap-4">
        <span className="font-display text-3xl text-[#8A5A00]">0{i + 1}</span>
        <h3 className="font-display text-4xl uppercase leading-none text-[#0D0D0B] sm:text-5xl">{job.title}</h3>
      </div>
    </Reveal>
    <div className={`grid grid-cols-2 gap-3 sm:gap-6 ${job.frames.length === 4 ? "lg:grid-cols-4" : "sm:grid-cols-2 lg:mx-auto lg:max-w-4xl"}`}>
      {job.frames.map((f, j) => <Frame key={f.src} f={f} delay={j * 0.1} testid={`before-after-job-${i}-${f.label.toLowerCase()}-${j}`} />)}
    </div>
  </div>
);

export const BeforeAfter = () => (
  <section id="results" data-testid="before-after-section" className="bg-[#F2EEE6] py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <div className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <Reveal>
          <Eyebrow dark>Before & After</Eyebrow>
          <h2 className="font-display mt-4 text-6xl uppercase leading-[0.9] text-[#0D0D0B] sm:text-8xl">Real jobs.<br />Real results.</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="max-w-xs text-base text-[#3a3a35]">Debris, brush and brick piles, gone. Here's what our customers' properties looked like before and after we came by.</p>
        </Reveal>
      </div>
      <div className="space-y-16">
        {JOBS.map((job, i) => <Job key={job.title} job={job} i={i} />)}
      </div>
    </div>
  </section>
);
